import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Header } from '@/components/shared';
import { useTheme } from '@/contexts/ThemeContext';
import { getApiUrl } from '@/config/api';
import { authenticatedFetch } from '@/utils/apiUtils';
import DayCell from './DayCell';
import DayDetail from './DayDetail';
import type { DayData } from './DayCell';
import { PROVIDER_LOGOS, ProviderLogo } from './DayCell';

type ViewMode = 'month' | 'week';
export type SourceKey = keyof typeof PROVIDER_LOGOS;

const ALL_SOURCES: SourceKey[] = ['garmin', 'terra_activity', 'sleep', 'catapult', 'hawkin'];
const WEEKDAY_LABELS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

function pad2(n: number): string {
    return n < 10 ? `0${n}` : String(n);
}

function toDateStr(y: number, m: number, d: number): string {
    return `${y}-${pad2(m)}-${pad2(d)}`;
}

function startOfWeek(date: Date): Date {
    const d = new Date(date);
    d.setDate(d.getDate() - d.getDay());
    return d;
}

const CalendarView: React.FC = () => {
    const { isDark } = useTheme();
    const today = new Date();
    const todayStr = toDateStr(today.getFullYear(), today.getMonth() + 1, today.getDate());

    const [year, setYear] = useState(today.getFullYear());
    const [month, setMonth] = useState(today.getMonth() + 1);
    const [viewMode, setViewMode] = useState<ViewMode>('month');
    const [weekStart, setWeekStart] = useState<Date>(() => startOfWeek(today));
    const [enabledSources, setEnabledSources] = useState<Set<SourceKey>>(new Set(ALL_SOURCES));

    const [days, setDays] = useState<Record<string, DayData>>({});
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [selectedDay, setSelectedDay] = useState<string | null>(null);

    const [userInfo] = useState<{ username: string; role?: string } | null>(() => {
        try { return JSON.parse(localStorage.getItem('user_info') || 'null'); } catch { return null; }
    });

    const toggleSource = (s: SourceKey) => {
        setEnabledSources(prev => {
            const next = new Set(prev);
            if (next.has(s)) next.delete(s); else next.add(s);
            return next;
        });
    };

    const fetchData = useCallback(async () => {
        setLoading(true);
        setError(null);

        let url: string;
        if (viewMode === 'week') {
            const from = new Date(weekStart);
            const to = new Date(weekStart);
            to.setDate(to.getDate() + 6);
            url = `/api/calendar/?from_date=${toDateStr(from.getFullYear(), from.getMonth() + 1, from.getDate())}&to_date=${toDateStr(to.getFullYear(), to.getMonth() + 1, to.getDate())}`;
        } else {
            url = `/api/calendar/?year=${year}&month=${month}`;
        }

        const result = await authenticatedFetch(getApiUrl(url), { cache: 'no-store' }, 'Calendar');
        if (result.success && result.data?.days) {
            setDays(result.data.days);
        } else {
            setError(result.error || 'Failed to load calendar data');
            setDays({});
        }
        setLoading(false);
    }, [year, month, viewMode, weekStart]);

    useEffect(() => { fetchData(); }, [fetchData]);

    const goToday = () => {
        setYear(today.getFullYear());
        setMonth(today.getMonth() + 1);
        setWeekStart(startOfWeek(today));
    };

    const goPrev = () => {
        if (viewMode === 'month') {
            if (month === 1) { setMonth(12); setYear(y => y - 1); }
            else setMonth(m => m - 1);
        } else {
            setWeekStart(prev => { const d = new Date(prev); d.setDate(d.getDate() - 7); return d; });
        }
    };

    const goNext = () => {
        if (viewMode === 'month') {
            if (month === 12) { setMonth(1); setYear(y => y + 1); }
            else setMonth(m => m + 1);
        } else {
            setWeekStart(prev => { const d = new Date(prev); d.setDate(d.getDate() + 7); return d; });
        }
    };

    const monthCells = useMemo(() => {
        const firstDay = new Date(year, month - 1, 1);
        const lastDay = new Date(year, month, 0);
        const startDow = firstDay.getDay();
        const daysInMonth = lastDay.getDate();
        const cells: Array<{ date: number; dateStr: string; isCurrentMonth: boolean }> = [];

        const prevMonthLast = new Date(year, month - 1, 0);
        for (let i = startDow - 1; i >= 0; i--) {
            const d = prevMonthLast.getDate() - i;
            const pm = month === 1 ? 12 : month - 1;
            const py = month === 1 ? year - 1 : year;
            cells.push({ date: d, dateStr: toDateStr(py, pm, d), isCurrentMonth: false });
        }
        for (let d = 1; d <= daysInMonth; d++) {
            cells.push({ date: d, dateStr: toDateStr(year, month, d), isCurrentMonth: true });
        }
        const remaining = 7 - (cells.length % 7);
        if (remaining < 7) {
            const nm = month === 12 ? 1 : month + 1;
            const ny = month === 12 ? year + 1 : year;
            for (let d = 1; d <= remaining; d++) {
                cells.push({ date: d, dateStr: toDateStr(ny, nm, d), isCurrentMonth: false });
            }
        }
        return cells;
    }, [year, month]);

    const weekCells = useMemo(() => {
        const cells: Array<{ date: number; dateStr: string; isCurrentMonth: boolean }> = [];
        for (let i = 0; i < 7; i++) {
            const d = new Date(weekStart);
            d.setDate(d.getDate() + i);
            cells.push({ date: d.getDate(), dateStr: toDateStr(d.getFullYear(), d.getMonth() + 1, d.getDate()), isCurrentMonth: true });
        }
        return cells;
    }, [weekStart]);

    const cells = viewMode === 'month' ? monthCells : weekCells;
    const rowCount = Math.ceil(cells.length / 7);

    const monthLabel = new Date(year, month - 1).toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
    const weekLabel = (() => {
        const ws = new Date(weekStart);
        const we = new Date(weekStart);
        we.setDate(we.getDate() + 6);
        const fmt = (d: Date) => d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
        return `${fmt(ws)} – ${fmt(we)}, ${we.getFullYear()}`;
    })();

    const borderColor = isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.08)';

    return (
        <div className="h-screen flex flex-col overflow-hidden">
            <Header userInfo={userInfo} isDark={isDark} />
            <div className="flex-1 flex flex-col min-h-0 px-1 pt-1 pb-0.5">
                {/* Toolbar */}
                <div className="flex flex-wrap items-center gap-3 mb-1 flex-shrink-0">
                    {/* Month nav */}
                    <div className="flex items-center gap-1.5">
                        <button onClick={goPrev}
                            className="w-7 h-7 flex items-center justify-center rounded-md cgm-text-secondary hover:cgm-text-primary transition-colors cursor-pointer border"
                            style={{ borderColor }}>
                            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" /></svg>
                        </button>
                        <span className="text-sm font-semibold cgm-text-primary min-w-[150px] text-center">
                            {viewMode === 'month' ? monthLabel : weekLabel}
                        </span>
                        <button onClick={goNext}
                            className="w-7 h-7 flex items-center justify-center rounded-md cgm-text-secondary hover:cgm-text-primary transition-colors cursor-pointer border"
                            style={{ borderColor }}>
                            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" /></svg>
                        </button>
                    </div>

                    {/* Divider */}
                    <div className="w-px h-5" style={{ backgroundColor: borderColor }} />

                    {/* Source filter checkboxes (logos as labels) */}
                    <div className="flex items-center gap-1">
                        {ALL_SOURCES.map(s => {
                            const on = enabledSources.has(s);
                            return (
                                <button
                                    key={s}
                                    onClick={() => toggleSource(s)}
                                    className={`w-7 h-7 flex items-center justify-center rounded-md border transition-all cursor-pointer ${on ? '' : 'opacity-30 grayscale'}`}
                                    style={{ borderColor: on ? borderColor : 'transparent' }}
                                    title={PROVIDER_LOGOS[s].alt}
                                >
                                    <ProviderLogo provider={s} size={16} />
                                </button>
                            );
                        })}
                        <span className="text-[10px] cgm-text-secondary opacity-50 ml-1 select-none">click to toggle</span>
                    </div>

                    {/* Spacer */}
                    <div className="flex-1" />

                    {/* Today + view toggle */}
                    <div className="flex items-center gap-2">
                        <button onClick={goToday}
                            className="px-2.5 py-1 text-xs font-medium rounded-md cgm-text-secondary hover:cgm-text-primary transition-colors cursor-pointer border"
                            style={{ borderColor }}>
                            Today
                        </button>
                        <div className="flex rounded-md overflow-hidden border" style={{ borderColor }}>
                            {(['month', 'week'] as ViewMode[]).map(v => (
                                <button key={v} onClick={() => setViewMode(v)}
                                    className={`px-2.5 py-1 text-xs font-medium transition-colors cursor-pointer capitalize ${
                                        viewMode === v ? 'bg-blue-600 text-white' : 'cgm-text-secondary hover:cgm-text-primary'
                                    }`}>
                                    {v}
                                </button>
                            ))}
                        </div>
                    </div>
                </div>

                {error && (
                    <div className="mb-1 p-2 rounded-lg bg-red-500/10 text-red-400 text-sm flex-shrink-0">{error}</div>
                )}

                {/* Calendar grid */}
                <div className={`flex-1 min-h-0 flex flex-col border overflow-hidden ${isDark ? 'border-white/[0.06]' : 'border-black/[0.06]'}`}>
                    <div className="grid grid-cols-7 flex-shrink-0">
                        {WEEKDAY_LABELS.map(d => (
                            <div key={d} className={`py-1 text-center text-[11px] font-medium cgm-text-secondary ${isDark ? 'bg-white/[0.02]' : 'bg-black/[0.02]'}`}>
                                {d}
                            </div>
                        ))}
                    </div>

                    {loading ? (
                        <div className="flex items-center justify-center flex-1">
                            <div className="w-6 h-6 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" />
                        </div>
                    ) : (
                        <div
                            className="flex-1 min-h-0 grid grid-cols-7"
                            style={{ gridTemplateRows: `repeat(${rowCount}, minmax(0, 1fr))` }}
                        >
                            {cells.map(cell => (
                                <div
                                    key={cell.dateStr}
                                    className="min-h-0 overflow-hidden cgm-bg-primary"
                                    style={{ borderRight: `1px solid ${isDark ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.06)'}`, borderBottom: `1px solid ${isDark ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.06)'}` }}
                                >
                                    <DayCell
                                        date={cell.date}
                                        dateStr={cell.dateStr}
                                        data={days[cell.dateStr] || null}
                                        isToday={cell.dateStr === todayStr}
                                        isCurrentMonth={cell.isCurrentMonth}
                                        isWeekView={viewMode === 'week'}
                                        onClick={setSelectedDay}
                                        enabledSources={enabledSources}
                                    />
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </div>

            {selectedDay && (
                <DayDetail
                    dateStr={selectedDay}
                    data={days[selectedDay] || null}
                    onClose={() => setSelectedDay(null)}
                />
            )}
        </div>
    );
};

export default CalendarView;
